import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn
} from '@repo/ui'
import { ChevronDown, ChevronRight, Sigma } from 'lucide-react'
import { useState } from 'react'
import type { ConversationComputedResult } from '@/api/conversation'

type ComputedTable = NonNullable<ConversationComputedResult['table']>

// computedResultMetric is the badge beside the title. Cells computed is the
// honest figure only when an aggregate actually parsed cells: tabularquery
// populates IncludedCells inside computeAgg's decimal loop, which a `count`
// returns before reaching and a plain `select` never enters at all. Reporting
// "0 cells" on the one surface whose whole purpose is to show that the
// arithmetic was real would be worse than reporting nothing, so those queries
// report the rows they matched instead.
export function computedResultMetric(table: ComputedTable): string {
  if (table.cellsComputed > 0) return `${table.cellsComputed} cells`
  return table.rowsMatched === 1 ? '1 row' : `${table.rowsMatched} rows`
}

// computedResultFooter states where the numbers came from and how much of the
// source they cover. It is the only place a reader can tell an aggregate over
// four matched rows from one over three hundred, or see that cells were left
// out of it.
export function computedResultFooter(table: ComputedTable): string {
  const parts = [table.sheet === '' ? table.fileName : `${table.fileName}, sheet ${table.sheet}`]
  parts.push(`${table.rowsMatched} of ${table.rowsTotal} rows matched`)
  for (const excluded of table.excludedCells) {
    parts.push(`${excluded.column}: ${excluded.count} non-numeric cells excluded`)
  }
  if (table.truncated) {
    parts.push(`showing ${table.rows.length} of ${table.truncatedFrom} result rows`)
  }
  return parts.join(' · ')
}

// ComputedResultTable renders the cells as text nodes, so a spreadsheet cell is
// displayed rather than interpreted. See ComputedResultFallback for why that
// distinction is load-bearing.
export function ComputedResultTable({ table }: { table: ComputedTable }) {
  return (
    <>
      {/* The rule sits on a static wrapper rather than on the table, because
          @repo/ui's Table already provides the one horizontal scroller and a
          border inside it would slide away from the edge on a wide result. */}
      <div className="border-t">
        <Table>
          <TableHeader>
            <TableRow>
              {table.columns.map((column, index) => (
                <TableHead key={`${column}:${index}`}>{column}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <TableCell key={cellIndex} className="tabular-nums">
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p data-testid="computed-result-footer" className="border-t px-3 py-2 text-xs text-muted-foreground">
        {computedResultFooter(table)}
      </p>
    </>
  )
}

// ComputedResultFallback renders a result written before the structured form
// existed, or one whose table was dropped to fit the payload budget.
//
// It is deliberately preformatted text and not a markdown render. The string is
// tabularquery's own RenderMarkdown output, whose cells come from an uploaded
// spreadsheet and whose escaper (result.go) handles only `|` and newlines,
// because its job was to keep a prompt's table from being corrupted, not to
// make document content safe to display. Passing it to MarkdownRenderer would
// let a cell reading `[Verify](https://elsewhere.example/?q=)` become a live
// link in the transcript, which is a new egress channel for document-controlled
// text rather than a formatting improvement. Monospace keeps the columns
// aligned and keeps every character literal.
export function ComputedResultFallback({ markdown }: { markdown: string }) {
  return (
    <pre className="overflow-x-auto px-3 py-2 font-mono text-xs leading-5 whitespace-pre text-muted-foreground">
      {markdown}
    </pre>
  )
}

// ComputedResultCard is the one surface in the product where "computed, not
// generated" is visible to a user, so it shows the table itself rather than the
// markdown the prompt path flattens it to.
export function ComputedResultCard({
  result,
  defaultOpen,
  onOpenSources
}: {
  result: ConversationComputedResult
  defaultOpen: boolean
  onOpenSources?: () => void
}) {
  const [open, setOpen] = useState(defaultOpen)
  const table = result.table ?? undefined
  // A result with no table still collapses. The degraded case is exactly the one
  // that produces the most results at once, so exempting it would put the
  // longest transcripts furthest from their answer.
  const title = table?.title ?? 'Computed result'
  const sourceCount = new Set(result.sourceDocumentIds.filter((docId) => Number.isSafeInteger(docId) && docId > 0)).size

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      role="group"
      aria-label={title}
      className="rounded-lg border bg-background"
    >
      <div className="flex items-center hover:bg-accent/40">
        <CollapsibleTrigger asChild>
          <button type="button" className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left">
            <Sigma className="size-4 shrink-0 text-primary" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{title}</span>
            {table !== undefined && (
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{computedResultMetric(table)}</span>
            )}
            <ChevronDown
              className={cn('size-4 shrink-0 transition-transform', open && 'rotate-180')}
              aria-hidden="true"
            />
          </button>
        </CollapsibleTrigger>
        {sourceCount > 0 && onOpenSources !== undefined && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="mr-1 h-8 shrink-0 gap-1 px-2 text-xs"
            onClick={onOpenSources}
          >
            View {sourceCount} {sourceCount === 1 ? 'source' : 'sources'}
            <ChevronRight className="size-3.5" aria-hidden="true" />
          </Button>
        )}
      </div>
      <CollapsibleContent>
        {table === undefined ? (
          <div className="border-t">
            <ComputedResultFallback markdown={result.markdown ?? ''} />
          </div>
        ) : (
          <ComputedResultTable table={table} />
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}
